import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { CatalogLanguage, WebsiteLanguage } from '../../Models/Models';
import { SiteContext } from '../../Contexts';
import { EntityColors } from '../../Themes/EntityColors';
import { Utils } from '../../Services/Utils';
import { WebsiteLanguageService } from '../../Services/WebsiteLanguageService';
import { languageName } from '../../Services/languageNames';
import TableShell from '../Table/TableShell';
import ToggleSwitch from '../ToggleSwitch';
import DeleteButton from '../Buttons/DeleteButton';
import ReusableModal from '../Modals/ReusableModal';
import CustomTooltip from '../Tooltips/CustomTooltip';
import { FlagImage, LanguagePickerModal } from './LanguagePicker';

function countLabel(count: number, forms: [string, string, string]): string {
    const form = count === 1 ? forms[0] : count >= 2 && count <= 4 ? forms[1] : forms[2];
    return `${count} ${form}`;
}

const LanguageTableComponent = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Jazyky');
    }, []);

    const { getCatalog, getSiteLanguages, addLanguage, updateLanguage, deleteLanguage } = WebsiteLanguageService();
    const { currentSite }: any = useContext(SiteContext);
    const { action, icon } = EntityColors.language;

    const [languages, setLanguages] = useState<WebsiteLanguage[] | null>(null);
    const [catalog, setCatalog] = useState<CatalogLanguage[]>([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<WebsiteLanguage | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);

    const load = async () => {
        const list = await getSiteLanguages(currentSite.id);
        setLanguages(list ?? []);
    };

    useEffect(() => {
        setLanguages(null);
        void load();
    }, [currentSite]);

    useEffect(() => {
        getCatalog().then(setCatalog);
    }, []);

    const addOptions = useMemo(
        () => catalog
            .filter(c => !languages?.some(l => l.languageCode === c.code))
            .map(c => c.code)
            .sort((a, b) => languageName(a).localeCompare(languageName(b), 'sk')),
        [catalog, languages]
    );

    const handleAdd = async (code: string) => {
        setShowAddModal(false);
        // New languages start CMS-only, so nothing reaches the public site until it is switched on.
        const created = await addLanguage({ websiteId: currentSite.id, languageCode: code, isPublic: false });
        if (created) await load();
    };

    const handleTogglePublic = async (language: WebsiteLanguage, isPublic: boolean) => {
        setBusyId(language.id);
        const updated = await updateLanguage({ id: language.id, isPublic });
        if (updated) setLanguages(prev => prev?.map(l => (l.id === updated.id ? updated : l)) ?? prev);
        setBusyId(null);
    };

    const handleDelete = async () => {
        if (!pendingDelete) return;
        const target = pendingDelete;
        setPendingDelete(null);
        if (await deleteLanguage(target.id)) await load();
    };

    const deleteDescription = (language: WebsiteLanguage): string => {
        const { textTranslationCount: texts, postTranslationCount: posts } = language;
        if (texts === 0 && posts === 0) {
            return 'Jazyk zatiaľ nemá žiadne preklady.';
        }
        const parts = [
            texts > 0 ? countLabel(texts, ['text', 'texty', 'textov']) : null,
            posts > 0 ? countLabel(posts, ['príspevok', 'príspevky', 'príspevkov']) : null,
        ].filter(Boolean).join(' a ');
        return `${parts} má preklad v tomto jazyku. Preklady zostanú zachované a bude ich možné naďalej upravovať, `
            + 'no nebudú sa zverejňovať a nové preklady v tomto jazyku nepôjde pridať.';
    };

    return (
        <>
            <TableShell
                title="Jazyky"
                icon={icon}
                accentColor={action}
                isLoading={languages === null}
                isEmpty={languages !== null && languages.length === 0}
                emptyMessage="Stránka zatiaľ nemá povolené žiadne jazyky"
                headerActions={
                    <button
                        type="button"
                        className="st-btn st-btn--fill"
                        style={{ '--btn-accent': action } as React.CSSProperties}
                        onClick={() => setShowAddModal(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} className="st-table-shell-create-icon" />
                        Pridať jazyk
                    </button>
                }
            >
                <Table responsive>
                    <thead>
                        <tr>
                            <th>Jazyk</th>
                            <th>Zverejnenie</th>
                            <th>Preklady</th>
                            <th className="w-1 text-center">Akcie</th>
                        </tr>
                    </thead>
                    <tbody>
                        {languages?.map(language => (
                            <tr key={language.id}>
                                <td>
                                    <div className="st-lang-row">
                                        <FlagImage code={language.languageCode} size="md" />
                                        <span className="st-lang-row__name">{languageName(language.languageCode)}</span>
                                        <span className="st-lang-row__code">{language.languageCode}</span>
                                    </div>
                                </td>
                                <td>
                                    <ToggleSwitch
                                        checked={language.isPublic}
                                        onChange={next => void handleTogglePublic(language, next)}
                                        accentColor={action}
                                        offLabel="Len CMS"
                                        onLabel="Verejný"
                                        disabled={busyId === language.id}
                                    />
                                </td>
                                <td className="st-lang-row__counts">
                                    {countLabel(language.textTranslationCount, ['text', 'texty', 'textov'])}
                                    {' · '}
                                    {countLabel(language.postTranslationCount, ['príspevok', 'príspevky', 'príspevkov'])}
                                </td>
                                <td className="text-center">
                                    <CustomTooltip placement="left" message="Odobrať jazyk">
                                        <span>
                                            <DeleteButton iconOnly onClick={() => setPendingDelete(language)} />
                                        </span>
                                    </CustomTooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableShell>

            <LanguagePickerModal
                show={showAddModal}
                title="Pridať jazyk"
                accentColor={action}
                options={addOptions}
                onHide={() => setShowAddModal(false)}
                onPick={code => void handleAdd(code)}
                searchable
            />

            <ReusableModal
                show={pendingDelete !== null}
                onHide={() => setPendingDelete(null)}
                onSubmit={() => void handleDelete()}
                title={pendingDelete ? `Odobrať jazyk ${languageName(pendingDelete.languageCode)}?` : undefined}
                description={pendingDelete ? deleteDescription(pendingDelete) : undefined}
            />
        </>
    );
};

export default LanguageTableComponent;
